import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import { toast } from "react-toastify";

import { CreateAssetClass } from "components/Prompts";
import { RoleName } from "constants/index";
import { usePrompt } from "hooks";
import { useInvestorsSelector, useUserIdSelector, useUserRoleSelector } from "store";
import {
  ActiveSliceType,
  AISource,
  ExposureMode,
  DriftRow,
  IPortfolioConstructionCompareGraph,
  IPortfolioConstructionPieChart,
  ObjectiveRow,
  PlannedDeal,
  PortfolioPermissions,
  SliceGroup,
  SliceInvestor,
  SliceSidebarTab,
  TargetPolicyItem
} from "store/types";

import { useObjectiveMutations } from "./useObjectiveMutations";
import { usePlannedDeals } from "./usePlannedDeals";
import { buildDriftRows, useSliceData } from "./useSliceData";
import { useSlices } from "./useSlices";
import { useTargetPolicy } from "./useTargetPolicy";

interface PortfolioConstructionContextValue {
  // Slice selection
  groups: SliceGroup[];
  investors: SliceInvestor[];
  sliceTab: SliceSidebarTab;
  setSliceTab: (tab: SliceSidebarTab) => void;
  selectedGroupId: SliceGroup["id"] | null;
  setSelectedGroupId: (id: SliceGroup["id"] | null) => void;
  selectedInvestorId: SliceInvestor["id"] | null;
  setSelectedInvestorId: (id: SliceInvestor["id"] | null) => void;
  onAddGroup: () => void;
  onAddInvestor: () => void;
  onEditSlice: (group: SliceGroup) => void;
  onDuplicateSlice: (group: SliceGroup) => void;
  onDeleteSlice: (group: SliceGroup) => void;

  // Permissions
  permissions: PortfolioPermissions;

  // Planned deals
  plannedDeals: PlannedDeal[];
  onAddDeal: () => void;
  onRemoveDeal: (deal: PlannedDeal) => void;
  onEditDeal: (deal: PlannedDeal) => void;

  // Drift exposure toggle
  exposureMode: ExposureMode;
  setExposureMode: (mode: ExposureMode) => void;

  // Active slice
  activeSliceKey: string | null;
  activeSliceType: ActiveSliceType | null;
  activeSliceName: string;
  targetPolicy: TargetPolicyItem[] | null;
  onOpenTargetPolicy: () => void;

  // Allocation drift
  aumError: string | null;
  clearAumError: () => void;
  onAddAssetClass: () => void;
  driftRows: DriftRow[];
  compareGraph: IPortfolioConstructionCompareGraph | undefined;
  compareGraphLoading: boolean;
  pieChart: IPortfolioConstructionPieChart[] | undefined;
  pieChartLoading: boolean;

  // Objectives
  aum: number | null;
  updateAum: (value: number) => Promise<void>;
  objectives: ObjectiveRow[] | null;
  isLoadingObjectives: boolean;
  objectiveErrors: Record<string, string>;
  clearObjectiveError: (key: string) => void;
  updateObjective: (key: string, value: number) => Promise<void>;
  applyAIObjective: (key: string, sourceLabel: string, value: number, url: string) => Promise<void>;
  resetObjective: (key: string) => Promise<void>;
  resetAllObjectives: () => Promise<void>;
  loadAISources: (key: string) => Promise<AISource[]>;
}

const PortfolioConstructionContext = createContext<PortfolioConstructionContextValue | null>(null);

export const PortfolioConstructionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const openPrompt = usePrompt();

  const userRole = useUserRoleSelector();
  const userId = useUserIdSelector();
  const roleName = userRole?.name;

  const permissions = useMemo(() => {
    const isAdmin = roleName === RoleName.Admin;
    const isTeamMember = roleName === RoleName.Analyst;
    const isTeamViewer = roleName === RoleName.TeamViewer;
    const isOwner = (group: SliceGroup) => group.createdById !== undefined && group.createdById === userId;
    const canManageOwn = (group: SliceGroup) => isAdmin || (isTeamMember && isOwner(group));

    return {
      canViewSlices: isAdmin || isTeamMember || isTeamViewer,
      canSelectSlice: isAdmin || isTeamMember || isTeamViewer,
      canCreateSlice: isAdmin || isTeamMember,
      canDuplicateSlice: isAdmin || isTeamMember,
      canEditSlice: canManageOwn,
      canArchiveSlice: canManageOwn,
      canEditTargetPolicy: isAdmin || isTeamMember,
      canEditObjectives: isAdmin || isTeamMember
    };
  }, [roleName, userId]);

  const storeInvestors = useInvestorsSelector();

  const investors = useMemo<SliceInvestor[]>(
    () => storeInvestors.map(({ id, name, company }) => ({ id, name, type: company || undefined })),
    [storeInvestors]
  );

  const [exposureMode, setExposureMode] = useState<ExposureMode>("nav");

  // Slice domain: sidebar selection, groups list, active slice, CRUD prompts.
  const {
    sliceTab,
    setSliceTab,
    selectedGroupId,
    setSelectedGroupId,
    selectedInvestorId,
    setSelectedInvestorId,
    groups,
    activeSliceKey,
    activeSliceType,
    activeSliceName,
    onAddGroup,
    onAddInvestor,
    onEditSlice,
    onDuplicateSlice,
    onDeleteSlice
  } = useSlices({ investors, permissions });

  // Target policy (loaded values + create/edit prompt).
  // Per-slice read data (objectives, AUM, raw drift figures, charts).
  const {
    objectives,
    aum,
    isLoadingObjectives,
    setObjectivesData,
    driftDisplayData,
    policyAssetClasses,
    reloadDrift,
    compareGraph,
    compareGraphLoading,
    pieChart,
    pieChartLoading
  } = useSliceData({
    activeSliceType,
    activeSliceKey,
    selectedInvestorId,
    selectedGroupId
  });

  // Target policy (loaded values + create/edit prompt). Asset classes come from the
  // display-mode breakdown so the form sees both system and custom classes with types.
  const { targetPolicy, onOpenTargetPolicy } = useTargetPolicy({
    activeSliceType,
    activeSliceKey,
    activeSliceName,
    selectedInvestorId,
    selectedGroupId,
    policyAssetClasses,
    canEditTargetPolicy: permissions.canEditTargetPolicy
  });

  // Drift rows = raw figures (display-mode) + targets (policy), per the exposure toggle.
  const driftRows = useMemo(
    () => buildDriftRows(driftDisplayData, exposureMode, targetPolicy),
    [driftDisplayData, exposureMode, targetPolicy]
  );

  // Planned deals — refetch the drift table after any change (it shifts nav_unfunded_planned).
  // Asset classes come from the display-mode breakdown so the form sees both system and
  // custom classes with their types.
  const { plannedDeals, onAddDeal, onEditDeal, onRemoveDeal } = usePlannedDeals({
    assetClasses: policyAssetClasses,
    investors,
    onDealsChanged: reloadDrift
  });

  // Objective edit flow (validation errors + upsert/reset mutations + AI sources).
  const {
    objectiveErrors,
    aumError,
    clearObjectiveError,
    clearAumError,
    updateAum,
    updateObjective,
    applyAIObjective,
    resetObjective,
    resetAllObjectives,
    loadAISources
  } = useObjectiveMutations({
    activeSliceType,
    activeSliceKey,
    selectedInvestorId,
    selectedGroupId,
    setObjectivesData,
    onAumChanged: reloadDrift
  });

  const onAddAssetClass = useCallback(async () => {
    const result = await openPrompt({
      Component: CreateAssetClass,
      props: { initialValue: "" }
    });
    if (!result) return;
    // The new asset class appears in the display-mode breakdown — refetch the drift table.
    reloadDrift();
    toast.success("Asset class created.");
  }, [openPrompt, reloadDrift]);

  const value = useMemo<PortfolioConstructionContextValue>(
    () => ({
      // ------- Slice selection -------
      groups,
      investors,
      sliceTab,
      setSliceTab,
      selectedGroupId,
      setSelectedGroupId,
      selectedInvestorId,
      setSelectedInvestorId,
      // ------- Slice CRUD -------
      onAddGroup,
      onAddInvestor,
      onEditSlice,
      onDuplicateSlice,
      onDeleteSlice,
      // ------- Permissions -------
      permissions,
      // ------- Planned deals -------
      plannedDeals,
      onAddDeal,
      onRemoveDeal,
      onEditDeal,
      // ------- Drift exposure toggle -------
      exposureMode,
      setExposureMode,
      activeSliceKey,
      activeSliceType,
      activeSliceName,
      // ------- Target policy -------
      targetPolicy,
      onOpenTargetPolicy,
      // ------- Allocation drift -------
      driftRows,
      compareGraph,
      compareGraphLoading,
      pieChart,
      pieChartLoading,
      // ------- Objectives -------
      aumError,
      clearAumError,
      onAddAssetClass,
      aum,
      updateAum,
      objectives,
      isLoadingObjectives,
      objectiveErrors,
      clearObjectiveError,
      updateObjective,
      applyAIObjective,
      resetObjective,
      resetAllObjectives,
      loadAISources
    }),
    [
      groups,
      investors,
      sliceTab,
      setSliceTab,
      selectedGroupId,
      selectedInvestorId,
      setSelectedGroupId,
      setSelectedInvestorId,
      onAddGroup,
      onAddInvestor,
      onEditSlice,
      onDuplicateSlice,
      onDeleteSlice,
      permissions,
      plannedDeals,
      onAddDeal,
      onRemoveDeal,
      onEditDeal,
      exposureMode,
      activeSliceKey,
      activeSliceType,
      activeSliceName,
      targetPolicy,
      onOpenTargetPolicy,
      aumError,
      clearAumError,
      onAddAssetClass,
      driftRows,
      compareGraph,
      compareGraphLoading,
      pieChart,
      pieChartLoading,
      aum,
      updateAum,
      objectives,
      isLoadingObjectives,
      objectiveErrors,
      clearObjectiveError,
      updateObjective,
      applyAIObjective,
      resetObjective,
      resetAllObjectives,
      loadAISources
    ]
  );

  return <PortfolioConstructionContext.Provider value={value}>{children}</PortfolioConstructionContext.Provider>;
};

export const usePortfolioConstructionContext = (): PortfolioConstructionContextValue => {
  const ctx = useContext(PortfolioConstructionContext);
  if (!ctx) {
    throw new Error("usePortfolioConstructionContext must be used inside <PortfolioConstructionProvider>");
  }
  return ctx;
};
